import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NotificationsService } from 'angular2-notifications';
import { FormBuilder, Validators } from '@angular/forms';
import { UserService } from '../../services/user.service';
import { OfframpService } from '../../services/offramp.service';
import { Ng4LoadingSpinnerService } from 'ng4-loading-spinner';

const defaults = {
  notif: {
    timeOut: 3000,
    showProgressBar: true,
    pauseOnHover: false,
    clickToClose: true,
    maxLength: 50
  }
};

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

@Component({
  selector: 'app-add-offramp-payout-gateway',
  templateUrl: './add-offramp-payout-gateway.component.html',
  styleUrls: ['./add-offramp-payout-gateway.component.css']
})
export class AddOfframpPayoutGatewayDialog {
  element: any = {};
  myForm: any;
  isEditMode: boolean = false;
  recordId: string = '';

  constructor(
    public dialogRef: MatDialogRef<AddOfframpPayoutGatewayDialog>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private notifSer: NotificationsService,
    public fb: FormBuilder,
    public userService: UserService,
    public offrampService: OfframpService,
    private spinnerService: Ng4LoadingSpinnerService
  ) {
    this.element = data.record || {};
    this.isEditMode = !!this.element._id;
    this.recordId = this.element._id || '';

    this.myForm = this.fb.group({
      name: [this.element.name || '', [Validators.required]],
      code: [this.element.code || '', [Validators.pattern(/^[A-Za-z0-9_]*$/)]],
      mid: [this.element.mid || ''],
      // Short name for this gateway + MID in the Telegram bot (/balance, /addbalance G:<short code>)
      short_code: [this.element.short_code || '', [Validators.pattern(/^[A-Za-z0-9]{2,10}$/)]],
      // Ops Telegram group is alerted when the wallet or live gateway balance goes below this; 0 = off
      low_balance_alert: [this.element.low_balance_alert || 0, [Validators.min(0)]],
      // Early warning to the ops Telegram group, set above the low balance alert; 0 = off
      mid_balance_alert: [this.element.mid_balance_alert || 0, [Validators.min(0)]],
      // Payouts above this need the beneficiary PAN at this gateway; orders without PAN go elsewhere; 0 = never
      pan_required_above: [this.element.pan_required_above || 0, [Validators.min(0)]],
      min_remaining_balance: [this.element.min_remaining_balance !== undefined ? this.element.min_remaining_balance : 500, [Validators.min(0)]],
      // Routing: lower priority is tried first; amount range the gateway accepts (0 = no limit)
      priority: [this.element.priority !== undefined ? this.element.priority : 100, [Validators.required, Validators.min(0), Validators.pattern(/^\d+$/)]],
      min_amount: [this.element.min_amount || 0, [Validators.min(0)]],
      max_amount: [this.element.max_amount || 0, [Validators.min(0)]],
      // Bank payout modes the gateway takes (none selected = all); UPI payouts only go to gateways with UPI supported
      supported_modes: [(this.element.supported_modes || []).filter((mode) => this.payoutModes.includes(mode))],
      upi_supported: [this.element.upi_supported === true],
      // UPI payout amount range, used instead of Min / Max Amount for UPI (0 = no limit)
      upi_min_amount: [this.element.upi_min_amount || 0, [Validators.min(0)]],
      upi_max_amount: [this.element.upi_max_amount || 0, [Validators.min(0)]],
      // Payouts this gateway may have PROCESSING at once; at the limit routing skips it (-1 = no limit)
      max_processing_orders: [this.element.max_processing_orders !== undefined && this.element.max_processing_orders !== null ? this.element.max_processing_orders : -1, [Validators.min(-1), Validators.pattern(/^-?\d+$/)]],
      // Gateway charge, configuration only for now
      charge_percent: [this.element.charge_percent || 0, [Validators.min(0), Validators.max(100)]],
      min_charge: [this.element.min_charge || 0, [Validators.min(0)]],
      // Daily window in IST, "HH:mm"; a start later than the end runs past midnight (e.g. 23:00 to 01:00)
      maintenance_start: [this.element.maintenance_start || '', [Validators.pattern(TIME_PATTERN)]],
      maintenance_end: [this.element.maintenance_end || '', [Validators.pattern(TIME_PATTERN)]],
      is_paused: [this.element.is_paused === true],
      pause_message: [this.element.pause_message || ''],
      // Alerts about this gateway's payouts (e.g. stuck PROCESSING) go to this WhatsApp group; empty = default group
      whatsapp_group_id: [this.element.whatsapp_group_id || '', [Validators.pattern(/^([0-9-]+@g\.us)?$/)]],
      is_active: [this.element.is_active !== undefined ? this.element.is_active : true],
    });

    // The wallet key is <code>_<mid>, so they can't change once the gateway wallet exists
    if (this.walletLocked) {
      this.myForm.get('code').disable();
      this.myForm.get('mid').disable();
    }
  }



  readonly payoutModes = ['IMPS', 'NEFT', 'RTGS'];

  get walletLocked(): boolean {
    return this.isEditMode && this.element.has_wallet === true;
  }

  saveGateway() {
    const { notif } = defaults;

    if (this.myForm.invalid) {
      this.notifSer.error('Error', 'Please fill all required fields', notif);
      return;
    }

    // getRawValue keeps code/mid when they are disabled (wallet locked)
    const values = this.myForm.getRawValue();
    const { maintenance_start, maintenance_end } = values;
    if (!!maintenance_start !== !!maintenance_end || (maintenance_start && maintenance_start === maintenance_end)) {
      this.notifSer.error('Error', 'Maintenance needs both From and To, and they must differ', notif);
      return;
    }
    const minAmount = Number(values.min_amount) || 0;
    const maxAmount = Number(values.max_amount) || 0;
    if (maxAmount > 0 && minAmount > maxAmount) {
      this.notifSer.error('Error', 'Min Amount can\'t be more than Max Amount', notif);
      return;
    }
    const lowBalanceAlert = Number(values.low_balance_alert) || 0;
    const midBalanceAlert = Number(values.mid_balance_alert) || 0;
    if (lowBalanceAlert > 0 && midBalanceAlert > 0 && midBalanceAlert <= lowBalanceAlert) {
      this.notifSer.error('Error', 'Mid Balance Alert must be above Low Balance Alert', notif);
      return;
    }
    const upiMinAmount = Number(values.upi_min_amount) || 0;
    const upiMaxAmount = Number(values.upi_max_amount) || 0;
    if (upiMaxAmount > 0 && upiMinAmount > upiMaxAmount) {
      this.notifSer.error('Error', 'UPI Min Amount can\'t be more than UPI Max Amount', notif);
      return;
    }

    this.spinnerService.show();

    const formData: any = {
      ...values,
      upi_supported: values.upi_supported === true,
      upi_min_amount: upiMinAmount,
      upi_max_amount: upiMaxAmount,
      code: String(values.code || '').trim().toUpperCase(),
      short_code: String(values.short_code || '').trim().toUpperCase(),
      priority: Number(values.priority),
      min_amount: minAmount,
      max_amount: maxAmount,
      max_processing_orders: values.max_processing_orders === '' || values.max_processing_orders === null ? -1 : Number(values.max_processing_orders),
      charge_percent: Number(values.charge_percent) || 0,
      pan_required_above: Number(values.pan_required_above) || 0,
      low_balance_alert: Number(values.low_balance_alert) || 0,
      mid_balance_alert: Number(values.mid_balance_alert) || 0,
      min_charge: Number(values.min_charge) || 0,
      _csrf: this.data._csrf
    };

    if (this.isEditMode) {
      formData._id = this.recordId;
      this.offrampService.updatePayoutGateway(formData).subscribe(
        (response) => {
          this.spinnerService.hide();
          if (response.status || response.success) {
            this.notifSer.success('Success', response.message || 'Payout gateway updated successfully', notif);
            this.dialogRef.close({ success: true, data: response });
          } else {
            this.notifSer.error('Error', response.message || 'Failed to update payout gateway', notif);
          }
        },
        (err) => {
          this.spinnerService.hide();
          this.notifSer.error('Error', (err.error && err.error.message) || 'Failed to update payout gateway', notif);
        }
      );
    } else {
      this.offrampService.createPayoutGateway(formData).subscribe(
        (response) => {
          this.spinnerService.hide();
          if (response.status || response.success) {
            this.notifSer.success('Success', response.message || 'Payout gateway created successfully', notif);
            this.dialogRef.close({ success: true, data: response });
          } else {
            this.notifSer.error('Error', response.message || 'Failed to create payout gateway', notif);
          }
        },
        (err) => {
          this.spinnerService.hide();
          this.notifSer.error('Error', (err.error && err.error.message) || 'Failed to create payout gateway', notif);
        }
      );
    }
  }

  onCancel() {
    this.dialogRef.close({ success: false });
  }
}



// WEBPACK FOOTER //
// ./src/app/offramp-payout-gateways/add-offramp-payout-gateway/add-offramp-payout-gateway.component.ts